// ─────────────────────────────────────────────────────────────────────
// ImageOptimizer — Optimisation universelle des photos avant analyse IA
// ─────────────────────────────────────────────────────────────────────
//
// Objectif : accepter TOUS les formats d'image (HEIC, JPEG, PNG, WebP, GIF)
// depuis la caméra ou la galerie, et produire une image optimale pour
// Claude Vision côté backend :
//   - Format JPEG (Claude ne supporte pas HEIC, PNG/WebP supportés mais lourds)
//   - Côté long ≤ 1600px (résolution amplement suffisante pour reconnaissance)
//   - Qualité JPEG 80% (~300-600 KB par photo en pratique)
//   - Base64 ≤ 4.5 MB (limite Anthropic API)
//
// Pourquoi 1600px ? Claude Vision est entraîné sur 768px max effectif, donc
// 1600px est largement suffisant. On garde un peu de marge pour les détails
// fins (textures, motifs).
//
// Si expo-image-manipulator n'est pas dispo (anciennes builds OTA), on dégrade
// gracieusement : retourne l'image telle quelle.

let ImageManipulator = null;
try {
  ImageManipulator = require('expo-image-manipulator');
} catch (e) {
  console.warn('[ImageOptimizer] expo-image-manipulator indisponible → fallback no-op');
}

const MAX_LONG_SIDE = 1600;
const JPEG_QUALITY  = 0.80;

/**
 * Optimise une image pour l'envoi à l'analyse IA.
 *
 * @param {string} uri - URI locale de l'image (file://, content://, ph://)
 * @returns {Promise<{uri: string, width: number, height: number, optimized: boolean}>}
 *   - uri        : URI de l'image optimisée (ou originale si fallback)
 *   - width/height : dimensions finales
 *   - optimized  : true si la lib a tourné, false si fallback no-op
 */
export async function optimizeImageForAI(uri) {
  if (!uri) {
    throw new Error('optimizeImageForAI: uri requise');
  }

  // Fallback : pas de lib dispo → retourne l'image originale
  if (!ImageManipulator || !ImageManipulator.manipulateAsync) {
    console.log('[ImageOptimizer] fallback no-op (lib indisponible)');
    return { uri, width: 0, height: 0, optimized: false };
  }

  try {
    // Étape 1 : Récupérer les dimensions originales (manipulate sans actions
    // suffit ; sur iOS HEIC, ça déclenche aussi la conversion)
    const initial = await ImageManipulator.manipulateAsync(
      uri,
      [],
      { compress: 1, format: ImageManipulator.SaveFormat.JPEG }
    );
    const { width: w0, height: h0 } = initial;

    // Étape 2 : Calculer le ratio de redimensionnement
    const longSide = Math.max(w0, h0);
    const actions = [];
    if (longSide > MAX_LONG_SIDE) {
      // Préserver l'aspect ratio, ne fixer qu'une dimension
      if (w0 >= h0) {
        actions.push({ resize: { width: MAX_LONG_SIDE } });
      } else {
        actions.push({ resize: { height: MAX_LONG_SIDE } });
      }
    }

    // Étape 3 : Appliquer redimensionnement + compression JPEG
    const final = await ImageManipulator.manipulateAsync(
      initial.uri,
      actions,
      {
        compress: JPEG_QUALITY,
        format: ImageManipulator.SaveFormat.JPEG,
      }
    );

    console.log(`[ImageOptimizer] ✓ ${w0}×${h0} → ${final.width}×${final.height}`);
    return {
      uri: final.uri,
      width: final.width,
      height: final.height,
      optimized: true,
    };
  } catch (e) {
    console.warn(`[ImageOptimizer] ⚠️ échec optimisation, fallback original : ${e.message}`);
    return { uri, width: 0, height: 0, optimized: false };
  }
}

/**
 * Détecte si une URI pointe sur un HEIC iOS (qui doit être converti).
 * Utile pour décider si l'optim est strictement nécessaire.
 */
export function isLikelyHeic(uri) {
  if (!uri) return false;
  const lower = String(uri).toLowerCase();
  return lower.endsWith('.heic') || lower.endsWith('.heif') || lower.includes('heic');
}
